'use client';

import { useState, useEffect } from 'react';

interface VerifyCustomCodeProps {
  email: string;
  userId: string;
  type: 'signup' | 'magic_login';
  name?: string;
  whatsapp?: string;
  onBack: () => void;
  onSuccess: () => void;
}

export default function VerifyCustomCode({ 
  email, 
  userId, 
  type,
  name,
  whatsapp,
  onBack, 
  onSuccess 
}: VerifyCustomCodeProps) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    console.log('[VerifyCustomCode] 🎯 Componente carregado:', { email, userId, type });
  }, [email, userId, type]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (code.length !== 6) {
      setError('O código deve ter 6 dígitos');
      return;
    }

    setLoading(true);

    try {
      console.log('[VerifyCustomCode] 🔑 Verificando código...');
      
      const response = await fetch('/api/auth/confirm-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          type,
          code,
          // Passar dados como fallback para criação de profile se necessário
          email,
          name,
          whatsapp,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Código inválido');
      }

      console.log('[VerifyCustomCode] ✅ Código verificado com sucesso');
      setSuccess('Verificação concluída!');
      
      // Pequeno delay para mostrar feedback
      setTimeout(() => {
        onSuccess();
      }, 500);

    } catch (err: any) {
      console.error('[VerifyCustomCode] ❌ Erro:', err);
      setError(err.message || 'Erro ao verificar código');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setSuccess(null);
    setResending(true);

    try {
      console.log('[VerifyCustomCode] 📨 Reenviando código...');
      
      const response = await fetch('/api/auth/request-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          type,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Erro ao reenviar');
      }

      console.log('[VerifyCustomCode] ✅ Código reenviado');
      setSuccess('Novo código enviado! Verifique seu WhatsApp.');

    } catch (err: any) {
      console.error('[VerifyCustomCode] ❌ Erro ao reenviar:', err);
      setError(err.message || 'Erro ao reenviar código');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full max-w-md rounded-2xl border border-gray-200 p-6 shadow-sm">
      <div className="mb-6 text-center">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h1 className="text-2xl font-semibold">Confirme seu WhatsApp</h1>
        <p className="text-sm text-gray-500 mt-2">
          Enviamos um código de 6 dígitos para seu WhatsApp
        </p>
        <p className="text-sm font-medium text-gray-700 mt-1">
          📱 {whatsapp || 'WhatsApp cadastrado'}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Verifique suas mensagens no WhatsApp
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
          {success}
        </div>
      )}

      <form onSubmit={handleVerify} className="space-y-4">
        <div className="grid gap-1.5">
          <label className="text-sm font-medium">Código de Verificação</label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="h-14 w-full rounded-md border border-gray-300 px-3 text-center text-2xl font-mono tracking-[0.5em] outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            placeholder="------"
            maxLength={6}
            required
            autoFocus
            autoComplete="one-time-code"
          />
          <p className="text-xs text-gray-500 text-center">
            O código expira em 10 minutos
          </p>
        </div>

        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="h-10 w-full rounded-md bg-black text-white font-medium hover:bg-black/90 disabled:opacity-50 transition-all"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verificando...
            </span>
          ) : 'Verificar Código'}
        </button>
      </form>

      <div className="mt-6 text-center text-sm text-gray-600 space-y-3">
        <div>
          <span className="text-gray-500">Não recebeu o código?</span>{' '}
          <button
            onClick={handleResend}
            disabled={resending}
            className="underline underline-offset-4 text-blue-600 hover:text-blue-700"
            type="button"
          >
            {resending ? 'Enviando...' : 'Reenviar código'}
          </button>
        </div>
        
        <button
          onClick={onBack}
          className="underline underline-offset-4 text-gray-500 hover:text-gray-700"
          type="button"
        >
          ← Voltar
        </button>
      </div>

      <div className="mt-6 p-3 bg-gray-50 rounded-lg text-xs text-gray-500">
        <p className="font-medium text-gray-700 mb-1">💡 Dicas:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Verifique suas mensagens no WhatsApp</li>
          <li>O código é válido por 10 minutos</li>
          <li>Você pode solicitar um novo código se necessário</li>
        </ul>
      </div>
    </div>
  );
}
